/**
 * Pagamento: carta Whop / bonifico totale / entrambi (canone + fee).
 * Accessibile solo dopo T&C (niente skip).
 */
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Loader2, Copy, Check, FileDown } from "lucide-react";
import { WhopCheckoutEmbed } from "@whop/checkout/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { customerStepPath, isCustomerStepDone, isCustomerStepReachable } from "@/components/onboarding/onboardingSteps";
import {
  confirmOnboardingCardPayment,
  createOnboardingWhopCheckout,
  downloadOnboardingContractPdf,
  fetchOnboardingByToken,
} from "@/lib/onboarding-public";
import {
  formatOnboardingMoney,
  ONBOARDING_SETUP_FEE_LABEL,
  onboardingBillingPeriodLabel,
  onboardingBillingPeriodSuffix,
  onboardingNeedsBankConfirm,
  resolveOnboardingPaymentMethod,
} from "@/types/onboarding";
import { cn } from "@/lib/utils";

const BANK_TRANSFER = {
  beneficiary: "Digital Partner Lab LLC",
  iban: "BE93 9051 3150 8967",
  bic: "TRWIBEB1XXX",
  bank: "Wise, Rue du Trône 100, 3rd floor, Brussels, 1050, Belgium",
  note: "Accetta EUR e altre valute",
} as const;

type PaymentUiStep = 1 | 2;

function CopyRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copiato`);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-border/50 last:border-0">
      <div className="min-w-0 space-y-0.5">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className={mono ? "text-sm font-mono break-all" : "text-sm break-words"}>{value}</p>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="shrink-0 rounded-xl h-8 gap-1.5"
        onClick={() => void copy()}
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        Copia
      </Button>
    </div>
  );
}

export default function OnboardingPaymentPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [whopEnvironment, setWhopEnvironment] = useState<"sandbox" | "production">(
    "production",
  );
  const [uiStep, setUiStep] = useState<PaymentUiStep>(1);
  const checkoutStartedRef = useRef(false);
  const confirmingCardRef = useRef(false);
  /** Riprova dopo errore: forza nuova checkout config */
  const forceNewCheckoutRef = useRef(false);
  /** Solo al primo load: se canone già ok, apri bonifico (poi tab carta resta navigabile) */
  const preferredBonificoOnceRef = useRef(false);
  const proceededAfterBonificoRef = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["onboarding-public", token],
    enabled: !!token,
    queryFn: () => fetchOnboardingByToken(token!),
    retry: false,
    staleTime: 0,
    refetchInterval: (q) => {
      const ob = q.state.data?.onboarding;
      if (!ob || ob.access_ready) return false;
      // Attesa canone, bonifico o conferma Core
      if (
        ob.current_step === "payment" ||
        ob.payment_succeeded_at ||
        resolveOnboardingPaymentMethod(ob.payment_method) === "bonifico"
      ) {
        return 3000;
      }
      return false;
    },
  });

  // Preferisci bonifico una sola volta all’ingresso (flusso entrambi)
  useEffect(() => {
    if (!data || preferredBonificoOnceRef.current) return;
    if (
      data.onboarding.payment_succeeded_at &&
      onboardingNeedsBankConfirm(data.onboarding) &&
      resolveOnboardingPaymentMethod(data.onboarding.payment_method) === "entrambi"
    ) {
      preferredBonificoOnceRef.current = true;
      setUiStep(2);
    }
  }, [data]);

  // Blocca skip: senza T&C → termini
  useEffect(() => {
    if (!token || !data) return;
    if (!isCustomerStepReachable("payment", data)) {
      navigate(customerStepPath(token, "terms"), { replace: true });
    }
  }, [token, data, navigate]);

  // Pagamenti ok → aggiorna UI e passa alla call (una sola volta)
  useEffect(() => {
    if (!data || !token || proceededAfterBonificoRef.current) return;
    if (!data.onboarding.access_ready) return;
    proceededAfterBonificoRef.current = true;
    const method = resolveOnboardingPaymentMethod(data.onboarding.payment_method);
    toast.success(
      method === "bonifico" ||
        (method === "entrambi" && Number(data.onboarding.setup_fee_amount) > 0)
        ? "Bonifico confermato — proseguiamo alla call"
        : "Pagamento confermato — proseguiamo alla call",
    );
    navigate(`/onboarding/${token}/call`);
  }, [data, token, navigate]);

  // Checkout Whop solo se T&C ok, non solo-bonifico, e non già pagato
  useEffect(() => {
    if (!token || !data?.onboarding.terms_accepted_at) return;
    if (resolveOnboardingPaymentMethod(data.onboarding.payment_method) === "bonifico") {
      return;
    }
    if (uiStep !== 1) return;
    if (data.onboarding.payment_succeeded_at) return;
    if (sessionId || checkoutError || checkoutStartedRef.current) return;

    checkoutStartedRef.current = true;
    setCreatingCheckout(true);
    setCheckoutError(null);

    void (async () => {
      try {
        const res = await createOnboardingWhopCheckout(token, {
          forceNew: forceNewCheckoutRef.current,
        });
        forceNewCheckoutRef.current = false;
        if (res.already_paid) {
          if (
            onboardingNeedsBankConfirm(data.onboarding) &&
            resolveOnboardingPaymentMethod(data.onboarding.payment_method) === "entrambi"
          ) {
            setUiStep(2);
          }
          await refetch();
          return;
        }
        if (!res.session_id) throw new Error("Sessione checkout Whop non ricevuta");
        if (res.environment === "sandbox" || res.environment === "production") {
          setWhopEnvironment(res.environment);
        }
        setSessionId(res.session_id);
      } catch (e) {
        checkoutStartedRef.current = false;
        const msg = e instanceof Error ? e.message : "Errore checkout Whop";
        setCheckoutError(msg);
        toast.error(msg);
      } finally {
        setCreatingCheckout(false);
      }
    })();
  }, [
    token,
    data?.onboarding.terms_accepted_at,
    data?.onboarding.payment_succeeded_at,
    data?.onboarding.setup_fee_amount,
    data?.onboarding.payment_method,
    sessionId,
    checkoutError,
    uiStep,
    refetch,
  ]);

  if (!token) return null;

  if (isLoading || error || !data) {
    return (
      <OnboardingShell
        token={token}
        step="payment"
        loading={isLoading}
        error={error instanceof Error ? error.message : error ? "Errore" : null}
        hideContinue
      >
        {null}
      </OnboardingShell>
    );
  }

  const { onboarding } = data;
  const paymentMethod = resolveOnboardingPaymentMethod(onboarding.payment_method);
  const setupFee = Number(onboarding.setup_fee_amount ?? 0) || 0;
  const hasTwoSteps = paymentMethod === "entrambi" && setupFee > 0;
  const bankOnly = paymentMethod === "bonifico";
  const bankTotal = Number(onboarding.price_amount) + setupFee;
  const cardPaid = !!onboarding.payment_succeeded_at;
  const bonificoOk = !!onboarding.setup_fee_received_at || !!onboarding.access_ready;
  const isTrialTenant = !!onboarding.tenant_is_trial;
  const termsOk = !!onboarding.terms_accepted_at;
  const secureOrigin = window.location.origin.replace(/^http:\/\//i, "https://");
  const returnUrl = `${secureOrigin}/onboarding/${token}/completato`;
  const causale = bankOnly
    ? `Onboarding Mindlead — ${onboarding.tenant_name}`
    : `Installazione Mindlead — ${onboarding.tenant_name}`;

  const goAfterCard = (_planId?: string, receiptId?: string) => {
    if (!token || confirmingCardRef.current) return;
    confirmingCardRef.current = true;
    void (async () => {
      try {
        const res = await confirmOnboardingCardPayment(token, {
          paymentId: receiptId || null,
        });
        if (res.awaiting_webhook) {
          toast.message("Pagamento inviato — conferma automatica in corso…");
          await refetch();
          return;
        }
        await refetch();
        toast.success("Abbonamento con carta confermato");
        if (hasTwoSteps) setUiStep(2);
        confirmingCardRef.current = false;
      } catch (e) {
        confirmingCardRef.current = false;
        toast.error(e instanceof Error ? e.message : "Conferma pagamento non riuscita");
      }
    })();
  };

  // Sblocca confirm dopo webhook / poll
  useEffect(() => {
    if (data?.onboarding.payment_succeeded_at) {
      confirmingCardRef.current = false;
    }
  }, [data?.onboarding.payment_succeeded_at]);

  const handleBack = () => {
    if (hasTwoSteps && uiStep === 2) {
      setUiStep(1);
      return;
    }
    navigate(`/onboarding/${token}/termini`);
  };

  const handleContinue = () => {
    if (hasTwoSteps && uiStep === 1) {
      if (!cardPaid) {
        toast.message("Completa prima il pagamento canone con carta");
        return;
      }
      setUiStep(2);
      return;
    }
    if (!isCustomerStepDone("payment", data)) {
      toast.message(
        bankOnly || (hasTwoSteps && !bonificoOk)
          ? "In attesa conferma bonifico da Mindlead"
          : "Completa il pagamento per continuare",
      );
      return;
    }
    navigate(`/onboarding/${token}/call`);
  };

  const downloadPdfMutation = useMutation({
    mutationFn: () => downloadOnboardingContractPdf(token!),
    onSuccess: ({ url }) => {
      window.open(url, "_blank", "noopener,noreferrer");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <OnboardingShell
      token={token}
      step="payment"
      tenantName={onboarding.tenant_name}
      payload={data}
      onBack={handleBack}
      onContinue={handleContinue}
      continueLabel={
        hasTwoSteps && uiStep === 1 ? "Bonifico" : "Continua"
      }
      continueDisabled={
        hasTwoSteps && uiStep === 1
          ? !cardPaid
          : !isCustomerStepDone("payment", data)
      }
    >
      {!termsOk ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3 space-y-3">
          <p className="text-sm text-amber-950 leading-relaxed">
            Accetta prima i termini per abilitare il pagamento.
          </p>
          <Button asChild size="sm" className="rounded-xl">
            <Link to={`/onboarding/${token}/termini`}>Vai ai termini</Link>
          </Button>
        </div>
      ) : (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-xl h-9"
            disabled={downloadPdfMutation.isPending}
            onClick={() => downloadPdfMutation.mutate()}
          >
            {downloadPdfMutation.isPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileDown className="mr-1.5 h-3.5 w-3.5" />
            )}
            Scarica contratto firmato
          </Button>
        </div>
      )}

      {/* Solo bonifico: schermata unica totale */}
      {bankOnly && termsOk ? (
        <>
          <section className="rounded-2xl border border-amber-200/80 bg-amber-50/40 p-5 space-y-3">
            <p className="text-sm font-medium text-amber-950">Pagamento via bonifico</p>
            <p className="text-2xl font-semibold tabular-nums text-amber-950">
              {formatOnboardingMoney(bankTotal, onboarding.currency)}
            </p>
            <p className="text-xs text-amber-900/80 leading-relaxed">
              Totale primo periodo: canone{" "}
              {formatOnboardingMoney(onboarding.price_amount, onboarding.currency)}{" "}
              {onboardingBillingPeriodSuffix(
                onboarding.billing_period,
                onboarding.billing_years,
              )}
              {setupFee > 0
                ? ` + ${formatOnboardingMoney(setupFee, onboarding.currency)} ${ONBOARDING_SETUP_FEE_LABEL}`
                : ""}
              . Effettua un unico bonifico alle coordinate sotto.
            </p>
            <div className="rounded-xl border border-amber-200/70 bg-white px-3.5">
              <CopyRow label="Intestatario" value={BANK_TRANSFER.beneficiary} />
              <CopyRow label="IBAN" value={BANK_TRANSFER.iban} mono />
              <CopyRow label="Swift / BIC" value={BANK_TRANSFER.bic} mono />
              <CopyRow label="Banca" value={BANK_TRANSFER.bank} />
              <CopyRow label="Causale" value={causale} />
              <CopyRow
                label="Importo"
                value={formatOnboardingMoney(bankTotal, onboarding.currency)}
                mono
              />
            </div>
            <p className="text-[11px] text-muted-foreground">{BANK_TRANSFER.note}</p>
          </section>
          <div className="rounded-2xl border border-border/70 bg-card p-5 text-center space-y-3">
            {bonificoOk ? (
              <>
                <p className="text-sm font-medium text-emerald-700">Bonifico confermato</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Mindlead ha registrato la ricezione. Puoi proseguire alla call di onboarding.
                </p>
                <Button asChild className="rounded-xl h-11">
                  <Link to={`/onboarding/${token}/call`}>Vai alla call</Link>
                </Button>
              </>
            ) : (
              <>
                <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm font-medium">In attesa conferma bonifico</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {isTrialTenant
                    ? "Puoi già accedere con le credenziali della prova. Quando il bonifico è confermato in Core, questa pagina si aggiorna."
                    : "Quando Mindlead registra il bonifico, riceverai l’email di accesso e potrai continuare."}
                </p>
              </>
            )}
          </div>
        </>
      ) : null}

      {hasTwoSteps && termsOk ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            {([1, 2] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setUiStep(s)}
                className={cn(
                  "min-h-[3.25rem] rounded-xl border px-3 py-2 text-left transition-colors",
                  uiStep === s
                    ? "border-emerald-600/40 bg-emerald-50 text-emerald-950"
                    : "border-border/70 bg-white text-muted-foreground hover:border-border hover:text-foreground",
                )}
              >
                <p className="text-[10px] uppercase tracking-wide opacity-80">
                  Step {s}
                </p>
                <p className="text-xs font-medium leading-snug">
                  {s === 1 ? "Abbonamento con carta" : "Installazione via bonifico"}
                </p>
              </button>
            ))}
          </div>
          <div className="flex gap-1.5">
            {([1, 2] as const).map((s) => (
              <div
                key={s}
                className={cn(
                  "h-1 flex-1 rounded-full",
                  s <= uiStep ? "bg-emerald-600" : "bg-stone-200",
                )}
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* Canone carta (carta o entrambi) */}
      {!bankOnly && uiStep === 1 && termsOk ? (
        <>
          <section className="rounded-2xl border border-border/70 bg-card p-5 space-y-3">
            <p className="text-sm font-medium">Abbonamento con carta</p>
            <p className="text-2xl font-semibold tabular-nums">
              {formatOnboardingMoney(onboarding.price_amount, onboarding.currency)}
              <span className="text-base font-medium text-muted-foreground">
                {" "}
                {onboardingBillingPeriodSuffix(
                  onboarding.billing_period,
                  onboarding.billing_years,
                )}
              </span>
            </p>
            <p className="text-xs text-muted-foreground">
              {onboardingBillingPeriodLabel(
                onboarding.billing_period,
                onboarding.billing_years,
              )}{" "}
              · addebito ricorrente via Whop
            </p>
            {cardPaid ? (
              <p className="text-xs text-emerald-700 font-medium">Canone confermato.</p>
            ) : null}
          </section>

          {cardPaid ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5 space-y-2">
              <p className="text-sm font-medium text-emerald-950">
                Pagamento con carta registrato correttamente
              </p>
              <p className="text-xs text-emerald-900/80 leading-relaxed">
                Abbonamento ricorrente attivo
                {onboarding.payment_succeeded_at
                  ? ` · confermato il ${new Date(onboarding.payment_succeeded_at).toLocaleString("it-IT", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}`
                  : ""}
                .{" "}
                {hasTwoSteps
                  ? "Puoi passare al bonifico installazione quando vuoi."
                  : "Puoi continuare con gli step successivi."}
              </p>
            </div>
          ) : (
            <div className="rounded-2xl border border-border/70 bg-card overflow-hidden min-h-[22rem]">
              {whopEnvironment === "sandbox" && (
                <div className="bg-amber-50 border-b border-amber-200/80 px-4 py-2 text-xs text-amber-950">
                  Modalità <strong>Whop Sandbox</strong> — nessun addebito reale. Usa carta{" "}
                  <code className="font-mono">4242 4242 4242 4242</code>.
                </div>
              )}
              {creatingCheckout && (
                <div className="flex flex-col items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Preparazione checkout…
                </div>
              )}
              {checkoutError && !creatingCheckout && (
                <div className="p-5 space-y-3 text-sm">
                  <p className="text-destructive break-words">{checkoutError}</p>
                  <button
                    type="button"
                    className="text-sky-700 underline text-xs"
                    onClick={() => {
                      forceNewCheckoutRef.current = true;
                      checkoutStartedRef.current = false;
                      setCheckoutError(null);
                      setSessionId(null);
                    }}
                  >
                    Riprova
                  </button>
                </div>
              )}
              {sessionId && !creatingCheckout && (
                <WhopCheckoutEmbed
                  sessionId={sessionId}
                  returnUrl={returnUrl}
                  environment={whopEnvironment}
                  theme="light"
                  themeOptions={{ accentColor: "green", borderRadius: 12 }}
                  prefill={
                    onboarding.signer_email ? { email: onboarding.signer_email } : undefined
                  }
                  skipRedirect
                  onComplete={goAfterCard}
                  onPaymentError={(err) => {
                    toast.error(err?.message || "Pagamento non riuscito");
                  }}
                />
              )}
            </div>
          )}

          {!hasTwoSteps && cardPaid && !onboarding.access_ready ? (
            <div className="rounded-2xl border border-border/70 bg-card p-5 text-center space-y-3">
              <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {isTrialTenant
                  ? "Stiamo confermando il pagamento. Resta attivo il login attuale: a conferma la prova diventa accesso illimitato."
                  : "Stiamo confermando il pagamento. Riceverai l’email con il link di accesso a breve."}
              </p>
            </div>
          ) : null}
        </>
      ) : null}

      {/* Bonifico fee (flusso entrambi) */}
      {!bankOnly && uiStep === 2 && hasTwoSteps && termsOk ? (
        <>
          <section className="rounded-2xl border border-amber-200/80 bg-amber-50/40 p-5 space-y-3">
            <p className="text-sm font-medium text-amber-950">
              {ONBOARDING_SETUP_FEE_LABEL} — bonifico
            </p>
            <p className="text-2xl font-semibold tabular-nums text-amber-950">
              {formatOnboardingMoney(setupFee, onboarding.currency)}
            </p>
            <p className="text-xs text-amber-900/80 leading-relaxed">
              Non viene addebitata sulla carta. Effettua un bonifico alle coordinate sotto e usa
              la causale indicata.{" "}
              {isTrialTenant
                ? "Hai già il login in prova: quando Mindlead registra il bonifico, la prova diventa accesso illimitato (stesse credenziali)."
                : "L’email con il link di accesso arriverà solo dopo che Mindlead avrà registrato la ricezione del bonifico."}
            </p>

            <div className="rounded-xl border border-amber-200/70 bg-white px-3.5">
              <CopyRow label="Intestatario" value={BANK_TRANSFER.beneficiary} />
              <CopyRow label="IBAN" value={BANK_TRANSFER.iban} mono />
              <CopyRow label="Swift / BIC" value={BANK_TRANSFER.bic} mono />
              <CopyRow label="Banca" value={BANK_TRANSFER.bank} />
              <CopyRow label="Causale" value={causale} />
              <CopyRow
                label="Importo"
                value={formatOnboardingMoney(setupFee, onboarding.currency)}
                mono
              />
            </div>
            <p className="text-[11px] text-muted-foreground">{BANK_TRANSFER.note}</p>
          </section>

          <div className="rounded-2xl border border-border/70 bg-card p-5 text-center space-y-3">
            {!cardPaid ? (
              <p className="text-sm text-amber-900">
                Completa prima lo step 1 (pagamento canone con carta). Poi resta in attesa della
                conferma del bonifico.
              </p>
            ) : bonificoOk ? (
              <>
                <p className="text-sm font-medium text-emerald-700">Bonifico confermato</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Mindlead ha registrato la ricezione. Puoi proseguire alla call di onboarding.
                </p>
                <Button asChild className="rounded-xl h-11">
                  <Link to={`/onboarding/${token}/call`}>Vai alla call</Link>
                </Button>
              </>
            ) : (
              <>
                <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm font-medium">In attesa conferma bonifico</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {isTrialTenant
                    ? "Puoi già accedere con le credenziali della prova. Quando il bonifico è confermato in Core, questa pagina si aggiorna e puoi continuare."
                    : "Quando Mindlead registra il bonifico, riceverai l’email di accesso e potrai continuare alla call."}
                </p>
                {isTrialTenant ? (
                  <Button asChild variant="outline" className="rounded-xl h-11">
                    <Link to="/construction/login">Accedi alla piattaforma</Link>
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </>
      ) : null}
    </OnboardingShell>
  );
}
